import { useEffect, useState } from 'react'
import { Save, Trash2, ShieldCheck, ExternalLink, KeyRound, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, UserPlus, RefreshCw, X, Clipboard, Rocket } from 'lucide-react'
import { api } from '../lib/api'
import type { Invite } from '../lib/api'
import { useAuthStore } from '../store/authStore'
import { useNavigate } from 'react-router-dom'

const inputClass =
  'w-full bg-warm-inset border border-warm-border rounded-xl px-4 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand-teal transition-colors'
const sectionClass = 'bg-warm-card border border-warm-border rounded-2xl p-6 mb-5 shadow-card'

interface OrgSettings {
  id: string
  name: string
  plan: string
  employee_count: number
  slack_workspace_id: string | null
  teams_tenant_id: string | null
  signup_country?: string | null
  industry?: string | null
}

interface SsoStatus {
  configured: boolean
  provider: string | null
  domain: string | null
}

export function SettingsPage() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const isAdmin = user?.role === 'admin'

  const [org, setOrg] = useState<OrgSettings | null>(null)
  const [orgName, setOrgName] = useState('')
  const [industry, setIndustry] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [erasing, setErasing] = useState(false)
  const [confirmErase, setConfirmErase] = useState(false)

  const [invites, setInvites] = useState<Invite[]>([])
  const [inviteEmails, setInviteEmails] = useState('')
  const [inviteRole, setInviteRole] = useState('employee')
  const [inviteSending, setInviteSending] = useState(false)
  const [inviteResults, setInviteResults] = useState<{ email: string; status: string }[] | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<{ newMembers: number; processed?: number; totalMembers: number } | null>(null)
  const [syncError, setSyncError] = useState<string | null>(null)

  const [ssoStatus, setSsoStatus] = useState<SsoStatus | null>(null)
  const [ssoOpen, setSsoOpen] = useState(false)
  const [ssoForm, setSsoForm] = useState({
    provider: 'okta',
    issuer_url: '',
    client_id: '',
    client_secret: '',
    domain: '',
  })
  const [ssoSaving, setSsoSaving] = useState(false)
  const [ssoSaved, setSsoSaved] = useState(false)
  const [ssoError, setSsoError] = useState<string | null>(null)
  const [copyStatus, setCopyStatus] = useState(false)

  const LAUNCH_MESSAGE = `Hi team! We're launching Duva 🕊, a light-touch AI coach that sends science-backed 'nudges' to help us build better work habits. You'll get a quick Slack message once or twice a week with a small, actionable tip. It takes less than 3 minutes — just tap 'Done' when you've tried it. Looking forward to growing together!`

  useEffect(() => {
    api.getOrgSettings()
      .then(r => {
        const s = r as OrgSettings
        setOrg(s)
        setOrgName(s.name)
        setIndustry(s.industry ?? '')
      })

    api.getSsoStatus()
      .then(r => setSsoStatus(r as SsoStatus))
      .catch(() => { /* non-critical */ })

    if (isAdmin) {
      api.getInvites()
        .then(r => setInvites(r.invites))
        .catch(() => { /* non-critical */ })
    }
  }, [isAdmin])

  async function handleSave() {
    setSaving(true)
    try {
      await api.updateOrgSettings({ name: orgName, industry: industry.trim() || null })
      const next = await api.getOrgSettings() as OrgSettings
      setOrg(next)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } finally {
      setSaving(false)
    }
  }

  async function handleInvite() {
    const emails = inviteEmails.split(/[\n,]+/).map(e => e.trim()).filter(Boolean)
    if (!emails.length) return
    setInviteSending(true)
    setInviteResults(null)
    try {
      const res = await api.sendInvites(emails, inviteRole)
      setInviteResults(res.results)
      if (!res.results.some(r => r.status === 'email_failed')) {
        setInviteEmails('')
      }
      const updated = await api.getInvites()
      setInvites(updated.invites)
    } finally {
      setInviteSending(false)
    }
  }

  async function handleRevokeInvite(id: string) {
    await api.revokeInvite(id)
    setInvites(prev => prev.filter(i => i.id !== id))
  }

  async function handleSync() {
    setSyncing(true)
    setSyncResult(null)
    setSyncError(null)
    try {
      const res = await api.syncMembers() as { newMembers: number; processed?: number; totalMembers: number }
      setSyncResult(res)
      const updated = await api.getOrgSettings() as OrgSettings
      setOrg(updated)
    } catch (e: unknown) {
      setSyncError(e instanceof Error ? e.message : 'Sync failed')
    } finally {
      setSyncing(false)
    }
  }

  async function handleSsoSave() {
    setSsoSaving(true)
    setSsoError(null)
    try {
      await api.configureSso(ssoForm)
      setSsoSaved(true)
      setSsoStatus({ configured: true, provider: ssoForm.provider, domain: ssoForm.domain })
      setTimeout(() => setSsoSaved(false), 3000)
    } catch (e: unknown) {
      setSsoError(e instanceof Error ? e.message : 'Failed to save SSO configuration')
    } finally {
      setSsoSaving(false)
    }
  }

  async function handleErase() {
    if (!confirmErase) { setConfirmErase(true); return }
    setErasing(true)
    try {
      await fetch('/api/me', { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('duva_token')}` } })
      logout()
      navigate('/login')
    } finally {
      setErasing(false)
    }
  }

  async function handleCopyLaunchMessage() {
    try {
      await navigator.clipboard.writeText(LAUNCH_MESSAGE)
      setCopyStatus(true)
      setTimeout(() => setCopyStatus(false), 2000)
    } catch (err) {
      console.error('Failed to copy message', err)
    }
  }

  const providerLabels: Record<string, string> = {
    okta: 'Okta',
    azure: 'Microsoft Entra ID (Azure AD)',
    google: 'Google Workspace',
    generic: 'Generic OIDC provider',
  }

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="mb-7">
        <h1 className="text-2xl font-bold text-ink" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>Settings</h1>
        <p className="text-sm mt-1" style={{color: '#6b8a8f'}}>Manage your organization and account</p>
      </div>

      <section className={sectionClass}>
        <h2 className="text-sm font-semibold text-ink mb-4">Organization</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-ink-soft mb-1.5">Organization name</label>
            <input
              type="text"
              value={orgName}
              onChange={e => setOrgName(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-ink-soft mb-1.5">Industry / category (optional)</label>
            <input
              type="text"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className={inputClass}
              placeholder="e.g. SaaS, Healthcare, Agency"
            />
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs text-ink-muted">
            <span>Plan: <span className="text-ink capitalize font-medium">{org?.plan ?? '…'}</span></span>
            <span>Members: <span className="text-ink font-medium">{org?.employee_count ?? '…'}</span></span>
            {org?.signup_country && (
              <span>Region: <span className="text-ink font-medium">{org.signup_country}</span></span>
            )}
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 text-white font-medium text-sm px-5 py-2 rounded-full transition-colors disabled:opacity-50 shadow-glow-teal" style={{background: "#2A7A8A"}}
          >
            <Save size={14} />
            {saved ? 'Saved!' : saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </section>

      {isAdmin && (
        <>
          <section className={sectionClass}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <UserPlus size={16} className="text-brand-teal" />
              <h2 className="text-sm font-semibold text-ink">Team Members</h2>
            </div>
            <button
              type="button"
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-1.5 text-xs text-ink-muted hover:text-brand-teal transition-colors disabled:opacity-50 font-medium"
            >
              <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Syncing…' : 'Sync from Slack'}
            </button>
          </div>

          {syncResult && (
            <div className="mb-4 text-xs text-emerald-800 bg-[#eef5ec] border border-[#B8C7B1] rounded-lg px-3 py-2">
              {syncResult.newMembers > 0
                ? `✓ Added ${syncResult.newMembers} new member(s) · ${syncResult.totalMembers} total`
                : (syncResult.processed ?? 0) > 0
                  ? `✓ Refreshed ${syncResult.processed} from Slack · ${syncResult.totalMembers} total`
                  : `✓ Up to date · ${syncResult.totalMembers} total`}
            </div>
          )}

          {syncError && (
            <div className="mb-4 text-xs text-red-800 bg-[#fdf2f2] border border-[#e8cfcf] rounded-lg px-3 py-2">
              {syncError}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="block text-xs text-ink-soft mb-1.5">Invite by email</label>
              <textarea
                value={inviteEmails}
                onChange={e => setInviteEmails(e.target.value)}
                placeholder="alice@company.com, bob@company.com&#10;(one per line or comma-separated)"
                rows={3}
                className={`${inputClass} resize-none`}
              />
            </div>
            <div className="flex items-center gap-3">
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
                className="bg-warm-inset border border-warm-border rounded-lg px-3 py-2 text-xs text-ink focus:outline-none focus:border-brand-teal"
              >
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
              <button
                type="button"
                onClick={handleInvite}
                disabled={inviteSending || !inviteEmails.trim()}
                className="flex items-center gap-2 text-white font-medium text-sm px-5 py-2 rounded-full transition-colors disabled:opacity-50 shadow-glow-teal" style={{background: "#2A7A8A"}}
              >
                <UserPlus size={13} />
                {inviteSending ? 'Sending…' : 'Send invites'}
              </button>
            </div>

            {inviteResults && (
              <div className="space-y-1">
                {inviteResults.map(r => (
                  <div
                    key={r.email}
                    className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border ${
                      r.status === 'sent'
                        ? 'bg-[#eef5ec] text-emerald-900 border-[#B8C7B1]'
                        : r.status === 'email_failed'
                          ? 'bg-[#fdf2f2] text-red-900 border-[#e8cfcf]'
                          : r.status === 'already_member'
                            ? 'bg-warm-inset text-ink-muted border-warm-border'
                            : 'bg-[#fdf8f0] text-amber-900 border-[#e8d4b8]'
                    }`}
                  >
                    {r.status === 'email_failed' ? (
                      <AlertCircle size={12} className="shrink-0" />
                    ) : (
                      <CheckCircle2 size={12} className="shrink-0" />
                    )}
                    <span>{r.email}</span>
                    <span className="ml-auto opacity-80 text-right">
                      {r.status === 'sent'
                        ? 'Invite sent'
                        : r.status === 'email_failed'
                          ? 'Email not sent — try again or check email setup'
                          : r.status === 'already_member'
                            ? 'Already a member'
                            : 'Already invited'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {invites.length > 0 && (
            <div className="mt-5 pt-5 border-t border-warm-border">
              <p className="text-xs font-semibold text-ink-soft mb-3">Pending invites ({invites.length})</p>
              <div className="space-y-2">
                {invites.map(invite => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between bg-warm-inset rounded-lg px-3 py-2 border border-warm-border"
                  >
                    <div>
                      <p className="text-xs text-ink font-medium">{invite.email}</p>
                      <p className="text-xs text-ink-muted mt-0.5">
                        {invite.role} · Invited by {invite.invited_by_name} · Expires {new Date(invite.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRevokeInvite(invite.id)}
                      className="text-ink-soft hover:text-red-700 transition-colors ml-3"
                      title="Revoke invite"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className={sectionClass}>
          <div className="flex items-center gap-2 mb-4">
            <Rocket size={16} className="text-brand-teal" />
            <h2 className="text-sm font-semibold text-ink">Launch Duva</h2>
          </div>
          <p className="text-xs text-ink-muted mb-4 leading-relaxed">
            Ready to roll out Duva to your organization? Copy this launch message to share on Slack or Email.
          </p>
          <div className="relative">
            <div className="w-full rounded-xl p-4 text-xs text-ink-muted bg-warm-inset border border-warm-border italic leading-relaxed">
              “{LAUNCH_MESSAGE}”
            </div>
            <button
              onClick={handleCopyLaunchMessage}
              className="mt-3 flex items-center gap-2 bg-[#2A7A8A] text-white font-medium text-xs px-4 py-2 rounded-lg transition-all hover:bg-[#236b7a] active:scale-95 shadow-glow-teal"
            >
              {copyStatus ? (
                <>
                  <CheckCircle2 size={13} />
                  Copied!
                </>
              ) : (
                <>
                  <Clipboard size={13} />
                  Copy message
                </>
              )}
            </button>
          </div>
        </section>
      </>
    )}

      <section className={sectionClass}>
        <h2 className="text-sm font-semibold text-ink mb-4">Integrations</h2>
        <div className="space-y-3">
          <IntegrationRow
            name="Slack"
            connected={!!org?.slack_workspace_id}
            connectUrl={api.getSlackInstallUrl()}
          />
          <IntegrationRow
            name="Microsoft Teams"
            connected={!!org?.teams_tenant_id}
            connectUrl={`${import.meta.env.VITE_API_URL ?? 'https://worker.duva.com'}/api/auth/teams/install`}
          />
        </div>
      </section>

      {isAdmin && (
        <section className={sectionClass}>
          <button
            type="button"
            onClick={() => setSsoOpen(o => !o)}
            className="w-full flex items-center justify-between gap-3 text-left"
          >
            <div className="flex items-center gap-3">
              <KeyRound size={16} className="text-brand-teal" />
              <div>
                <p className="text-sm font-semibold text-ink">Single Sign-On (SSO)</p>
                <p className="text-xs text-ink-muted mt-0.5">
                  {ssoStatus?.configured
                    ? `Configured — ${providerLabels[ssoStatus.provider ?? ''] ?? ssoStatus.provider} · ${ssoStatus.domain}`
                    : 'Not configured — Enterprise feature'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {ssoStatus?.configured && (
                <span className="text-xs bg-[#eef5ec] text-emerald-800 border border-[#B8C7B1] px-2.5 py-1 rounded-full font-medium">
                  ✓ Active
                </span>
              )}
              {ssoOpen
                ? <ChevronUp size={15} className="text-ink-soft" />
                : <ChevronDown size={15} className="text-ink-soft" />}
            </div>
          </button>

          {ssoOpen && (
            <div className="mt-5 pt-5 border-t border-warm-border space-y-4">
              <p className="text-xs text-ink-muted leading-relaxed">
                Once configured, employees can sign in via their corporate IdP instead of Slack. The SSO URL to share with your IT team will be{' '}
                <code className="text-brand-teal bg-warm-inset px-1.5 py-0.5 rounded text-xs border border-warm-border">
                  {`https://worker.duva.com/api/sso/login?domain=<your-domain>`}
                </code>
              </p>

              <div>
                <label className="block text-xs text-ink-soft mb-1.5">Identity Provider</label>
                <select
                  value={ssoForm.provider}
                  onChange={e => setSsoForm(f => ({ ...f, provider: e.target.value }))}
                  className={inputClass}
                >
                  <option value="okta">Okta</option>
                  <option value="azure">Microsoft Entra ID (Azure AD)</option>
                  <option value="google">Google Workspace</option>
                  <option value="generic">Generic OIDC</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-ink-soft mb-1.5">
                  Corporate email domain
                  <span className="text-ink-faint ml-1">(e.g. acme.com)</span>
                </label>
                <input
                  type="text"
                  placeholder="acme.com"
                  value={ssoForm.domain}
                  onChange={e => setSsoForm(f => ({ ...f, domain: e.target.value }))}
                  className={`${inputClass} font-mono`}
                />
                <p className="text-xs text-ink-muted mt-1">Users with this email domain will see &quot;Sign in with SSO&quot; on the login page.</p>
              </div>

              <div>
                <label className="block text-xs text-ink-soft mb-1.5">
                  {ssoForm.provider === 'okta' ? 'Okta Issuer URL' :
                   ssoForm.provider === 'azure' ? 'Azure Tenant ID / Metadata URL' :
                   ssoForm.provider === 'google' ? 'Google OIDC Issuer' : 'OIDC Issuer URL'}
                </label>
                <input
                  type="url"
                  placeholder={
                    ssoForm.provider === 'okta' ? 'https://yourorg.okta.com/oauth2/default' :
                    ssoForm.provider === 'azure' ? 'https://login.microsoftonline.com/{tenant-id}/v2.0' :
                    ssoForm.provider === 'google' ? 'https://accounts.google.com' :
                    'https://your-idp.example.com'
                  }
                  value={ssoForm.issuer_url}
                  onChange={e => setSsoForm(f => ({ ...f, issuer_url: e.target.value }))}
                  className={`${inputClass} font-mono`}
                />
              </div>

              <div>
                <label className="block text-xs text-ink-soft mb-1.5">Client ID</label>
                <input
                  type="text"
                  placeholder="0oa1b2c3d4e5..."
                  value={ssoForm.client_id}
                  onChange={e => setSsoForm(f => ({ ...f, client_id: e.target.value }))}
                  className={`${inputClass} font-mono`}
                />
              </div>

              <div>
                <label className="block text-xs text-ink-soft mb-1.5">Client Secret</label>
                <input
                  type="password"
                  placeholder="••••••••••••••••"
                  value={ssoForm.client_secret}
                  onChange={e => setSsoForm(f => ({ ...f, client_secret: e.target.value }))}
                  className={inputClass}
                />
                <p className="text-xs text-ink-muted mt-1">Stored encrypted at rest in Cloudflare KV. Never logged.</p>
              </div>

              <div className="bg-warm-inset border border-warm-border rounded-xl p-4">
                <p className="text-xs font-semibold text-ink mb-2">Redirect URI to register in your IdP:</p>
                <code className="text-xs text-brand-teal break-all">
                  https://worker.duva.com/api/sso/callback
                </code>
              </div>

              {ssoError && (
                <div className="flex items-center gap-2 text-red-800 bg-[#fdf2f2] border border-[#e8cfcf] rounded-xl px-4 py-3">
                  <AlertCircle size={14} className="shrink-0" />
                  <p className="text-xs">{ssoError}</p>
                </div>
              )}

              <button
                type="button"
                onClick={handleSsoSave}
                disabled={ssoSaving || !ssoForm.issuer_url || !ssoForm.client_id || !ssoForm.client_secret || !ssoForm.domain}
                className="flex items-center gap-2 text-white font-medium text-sm px-5 py-2 rounded-full transition-colors disabled:opacity-50 shadow-glow-teal" style={{background: "#2A7A8A"}}
              >
                {ssoSaved
                  ? <><CheckCircle2 size={14} /> SSO configured!</>
                  : ssoSaving
                  ? <><Save size={14} /> Saving…</>
                  : <><Save size={14} /> Save SSO configuration</>
                }
              </button>
            </div>
          )}
        </section>
      )}

      <section className={sectionClass}>
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={16} className="text-brand-teal" />
          <h2 className="text-sm font-semibold text-ink">Security & Compliance</h2>
        </div>
        <div className="space-y-2 text-xs text-ink-muted">
          <p>✓ All data encrypted in transit (TLS) and at rest (D1)</p>
          <p>✓ No passwords stored — identity delegated to Slack/Teams/SSO</p>
          <p>✓ GDPR: right to erasure available below</p>
          <p>✓ Audit log: every mutation logged with actor + timestamp</p>
          <a
            href="/security"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-brand-teal hover:text-brand-teal-hover mt-2 transition-colors font-medium"
          >
            <ExternalLink size={11} /> View security whitepaper
          </a>
        </div>
      </section>

      <section className={sectionClass}>
        <h2 className="text-sm font-semibold text-ink mb-3">Account</h2>
        <div className="text-xs text-ink-muted space-y-1 mb-4">
          <p>Name: <span className="text-ink">{user?.name}</span></p>
          <p>Email: <span className="text-ink">{user?.email}</span></p>
          <p>Role: <span className="text-ink capitalize">{user?.role}</span></p>
        </div>
      </section>

      <section className="bg-[#fdf2f2] border border-[#e8cfcf] rounded-2xl p-6">
        <h2 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
          <Trash2 size={14} /> Danger Zone
        </h2>
        <p className="text-xs text-ink-muted mb-4">
          Erase your personal data (GDPR right to erasure). This removes your name, email, and delivery history. Organization data is retained.
        </p>
        {confirmErase && (
          <p className="text-xs text-red-800 mb-3 font-medium">⚠️ Are you sure? This cannot be undone.</p>
        )}
        <button
          type="button"
          onClick={handleErase}
          disabled={erasing}
          className="flex items-center gap-2 bg-white hover:bg-[#fce8e8] border border-[#e8cfcf] text-red-800 font-medium text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
        >
          <Trash2 size={13} />
          {erasing ? 'Erasing…' : confirmErase ? 'Confirm erase my data' : 'Erase my personal data'}
        </button>
      </section>
    </div>
  )
}

function IntegrationRow({ name, connected, connectUrl }: {
  name: string; connected: boolean; connectUrl: string
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm text-ink font-medium">{name}</p>
        <p className="text-xs text-ink-muted">{connected ? 'Connected' : 'Not connected'}</p>
      </div>
      {connected ? (
        <span className="text-xs bg-[#eef5ec] text-emerald-800 border border-[#B8C7B1] px-2.5 py-1 rounded-full font-medium">
          ✓ Connected
        </span>
      ) : (
        <a
          href={connectUrl}
          className="text-xs text-white font-medium px-4 py-1.5 rounded-full transition-colors shadow-glow-teal"
          style={{ background: '#2A7A8A' }}
        >
          Connect
        </a>
      )}
    </div>
  )
}
